import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Stack,
  Divider,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Edit as EditIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  NavigateNext,
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { useSnackbar } from 'notistack';
import apiService from '../services/apiService';
import { Editor as TinyMCEEditor } from '@tinymce/tinymce-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Link as RouterLink } from 'react-router-dom';

interface TrainingContent {
  id: number;
  title: string;
  description: string;
  content_type: string;
  category_id: string;
  content?: string;
  content_url?: string;
  vote_count: number;
  user_vote?: number;
  active: boolean;
  publish_start?: string;
  publish_end?: string;
}

interface Category {
  id: string;
  name: string;
  description?: string;
}

interface FormData {
  title: string;
  description: string;
  content_type: string;
  category_id: string;
  content: string;
  content_url: string;
  active: boolean;
  publish_start: Date | null;
  publish_end: Date | null;
}

const defaultFormData: FormData = {
  title: '',
  description: '',
  content_type: 'embed',
  category_id: '',
  content: '',
  content_url: '',
  active: true,
  publish_start: new Date(),
  publish_end: null,
};

// Custom date picker component to avoid type errors
const CustomDatePicker: React.FC<{
  selected: Date | null;
  onChange: (date: Date | null) => void;
  dateFormat?: string;
  className?: string;
  placeholderText?: string;
}> = ({ selected, onChange, dateFormat, className, placeholderText }) => {
  return (
    <div style={{ position: 'relative', width: '100%', backgroundColor: '#fff', zIndex: 10 }}>
      <DatePicker
        selected={selected}
        onChange={onChange}
        dateFormat={dateFormat || "MMMM d, yyyy"}
        className={className || "w-full p-2 border border-gray-300 rounded"}
        placeholderText={placeholderText}
        popperProps={{
          positionFixed: true
        }}
        popperModifiers={[
          {
            name: 'preventOverflow',
            options: {
              enabled: true,
              escapeWithReference: false,
              boundary: window
            },
          },
        ]}
        wrapperClassName="date-picker-wrapper"
      />
    </div>
  );
};

const TrainingContentPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [content, setContent] = useState<TrainingContent | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [originalFormData, setOriginalFormData] = useState<FormData>(defaultFormData);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [customCategoryDescription, setCustomCategoryDescription] = useState('');

  const isNew = !id;
  const searchParams = new URLSearchParams(location.search);
  const isEditing = isNew || searchParams.get('edit') === 'true';

  useEffect(() => {
    // Redirect non-admin users
    if (!isAdmin()) {
      navigate('/app/help');
      return;
    }

    fetchCategories();
    if (!isNew) {
      fetchContent();
    } else {
      // Initialize form data for new content
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const newFormData = {
        ...defaultFormData,
        publish_start: today,
      };
      setFormData(newFormData);
      setOriginalFormData(newFormData);
      setIsLoading(false);
    }
  }, [id, isAdmin]);

  const fetchContent = async () => {
    try {
      const response = await apiService.get<TrainingContent>(`/api/training/content/${id}`);
      setContent(response.data);
      const newFormData = {
        title: response.data.title,
        description: response.data.description,
        content_type: response.data.content_type,
        category_id: response.data.category_id,
        content: response.data.content || '',
        content_url: response.data.content_url || '',
        active: response.data.active,
        publish_start: response.data.publish_start ? new Date(response.data.publish_start) : null,
        publish_end: response.data.publish_end ? new Date(response.data.publish_end) : null,
      };
      setFormData(newFormData);
      setOriginalFormData(newFormData);
    } catch (error) {
      console.error('Error loading training content:', error);
      enqueueSnackbar('Error loading training content', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.get<Category[]>('/api/training/categories');
      setCategories(response.data);
      // If we're creating new content and there are categories, default to the first one
      if (isNew && response.data.length > 0) {
        setFormData(prev => ({ ...prev, category_id: response.data[0].id }));
        setOriginalFormData(prev => ({ ...prev, category_id: response.data[0].id }));
      }
    } catch (error) {
      enqueueSnackbar('Error loading categories', { variant: 'error' });
    }
  };

  const handleVote = async (vote: number) => {
    if (!content) return;

    try {
      await apiService.post(`/api/training/content/${content.id}/vote`, { vote });
      fetchContent();
    } catch (error) {
      enqueueSnackbar('Error submitting vote', { variant: 'error' });
    }
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  const isFormValid = () => {
    return formData.title.trim() !== '' && formData.category_id !== '';
  };

  const handleCancel = () => {
    if (isNew) {
      navigate('/app/help?tab=training');
    } else {
      navigate('/app/help?tab=training');
      setFormData(originalFormData);
    }
  };

  const handleSave = async () => {
    try {
      let categoryId = formData.category_id;

      // Create custom category if needed
      if (showCustomCategory && customCategory.trim()) {
        const categoryResponse = await apiService.post<Category>('/api/training/categories', {
          name: customCategory.trim(),
          description: customCategoryDescription.trim() || undefined
        });
        categoryId = categoryResponse.data.id;
      }

      const payload = {
        ...formData,
        category_id: categoryId
      };

      if (isNew) {
        await apiService.post('/api/training/content', payload);
      } else {
        await apiService.put(`/api/training/content/${id}`, payload);
      }

      enqueueSnackbar('Training content saved successfully', { variant: 'success' });
      navigate('/app/help?tab=training');
    } catch (error) {
      console.error('Error saving training content:', error);
      enqueueSnackbar('Error saving training content', { variant: 'error' });
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiService.post<{ url: string }>('/api/training/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setFormData(prev => ({ ...prev, content_url: response.data.url }));
    } catch (error) {
      enqueueSnackbar('Error uploading file', { variant: 'error' });
    }
  };

  const handleEditorChange = (content: string) => {
    setFormData(prev => ({ ...prev, content }));
  };

  const handleCategoryChange = async (value: string) => {
    if (value === 'other') {
      setShowCustomCategory(true);
      setFormData(prev => ({ ...prev, category_id: '' }));
      setCustomCategory('');
      setCustomCategoryDescription('');
    } else {
      setShowCustomCategory(false);
      setFormData(prev => ({ ...prev, category_id: value }));
    }
  };

  const handleCustomCategoryChange = (value: string) => {
    setCustomCategory(value);
    // When a valid category name is entered, update the form validation
    if (value.trim()) {
      const categoryId = value.trim().toLowerCase().replace(' ', '-');
      setFormData(prev => ({ ...prev, category_id: categoryId }));
    } else {
      setFormData(prev => ({ ...prev, category_id: '' }));
    }
  };

  const renderContent = () => {
    if (!content) return null;

    switch (content.content_type) {
      case 'embed':
        return (
          <Box 
            className="embedded-content" 
            sx={{ 
              width: '100%',
              '& iframe': {
                border: 'none',
                borderRadius: 1,
                boxShadow: 1
              }
            }}
            dangerouslySetInnerHTML={{ __html: content.content || '' }}
          />
        );

      case 'html':
        return (
          <Box 
            className="html-content"
            dangerouslySetInnerHTML={{ __html: content.content || '' }}
          />
        );

      case 'pdf':
        return content.content_url && (
          <iframe
            src={content.content_url}
            style={{ width: '100%', height: '800px', border: 'none' }}
            title="PDF Content"
          />
        );

      case 'video':
        return content.content_url && (
          content.content_url.startsWith('http') ? (
            <iframe
              src={content.content_url}
              style={{ width: '100%', height: '600px', border: 'none' }}
              title="Video Content"
              allowFullScreen
            />
          ) : (
            <video
              controls
              style={{ width: '100%', maxHeight: '600px' }}
            >
              <source src={content.content_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
          <Link component={RouterLink} to="/app/help?tab=training" color="inherit">
            Help
          </Link>
          <Typography color="text.primary">
            {isNew ? 'Create Training Content' : content?.title || 'Training Content'}
          </Typography>
        </Breadcrumbs>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            {isEditing ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'flex-start' }}>
                  <Stack direction="row" spacing={1} sx={{ flex: 1, maxWidth: '66%' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Publish Start</Typography>
                      <CustomDatePicker
                        selected={formData.publish_start}
                        onChange={(date) => setFormData({ ...formData, publish_start: date })}
                        dateFormat="MMMM d, yyyy"
                        placeholderText="Select start date"
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Publish End</Typography>
                      <CustomDatePicker
                        selected={formData.publish_end}
                        onChange={(date) => setFormData({ ...formData, publish_end: date })}
                        dateFormat="MMMM d, yyyy"
                        placeholderText="Select end date (optional)"
                      />
                    </Box>
                  </Stack>
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.active}
                          onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        />
                      }
                      label="Active"
                    />
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  sx={{ mb: 2 }}
                  required
                  error={formData.title.trim() === ''}
                  helperText={formData.title.trim() === '' ? 'Title is required' : ''}
                />

                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  sx={{ 
                    mb: 2,
                    '& .MuiInputBase-root': {
                      minHeight: '100px'
                    }
                  }}
                  multiline
                  rows={3}
                />

                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                  <FormControl sx={{ width: '300px' }} error={formData.category_id === ''}>
                    <InputLabel required>Category</InputLabel>
                    <Select
                      value={formData.category_id === '' ? 'other' : formData.category_id}
                      label="Category"
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      sx={{ height: '44px' }}
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                      <MenuItem value="other">+ Add New Category</MenuItem>
                    </Select>
                    {formData.category_id === '' && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                        Category is required
                      </Typography>
                    )}
                  </FormControl>
                  {showCustomCategory && (
                    <TextField
                      label="New Category Name"
                      value={customCategory}
                      onChange={(e) => handleCustomCategoryChange(e.target.value)}
                      size="small"
                      sx={{ flex: 1, '& .MuiInputBase-root': { height: '44px' } }}
                      required
                      error={customCategory.trim() === ''}
                      helperText={customCategory.trim() === '' ? 'Category name is required' : ''}
                    />
                  )}
                </Box>

                <FormControl sx={{ width: '300px', mb: 2 }}>
                  <InputLabel>Content Type</InputLabel>
                  <Select
                    value={formData.content_type}
                    label="Content Type"
                    onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
                    sx={{ height: '44px' }}
                  >
                    <MenuItem value="html">Rich Text</MenuItem>
                    <MenuItem value="embed">Embedded Content</MenuItem>
                    <MenuItem value="pdf">PDF</MenuItem>
                    <MenuItem value="video">Video</MenuItem>
                  </Select>
                </FormControl>

                {formData.content_type === 'html' && (
                  <div className="html-editor">
                    <TinyMCEEditor
                      apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
                      value={formData.content}
                      onEditorChange={handleEditorChange}
                      init={{
                        height: 500,
                        menubar: false,
                        plugins: [
                          'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                          'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                          'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                        ],
                        toolbar: 'undo redo | blocks | ' +
                          'bold italic forecolor | alignleft aligncenter ' +
                          'alignright alignjustify | bullist numlist outdent indent | ' +
                          'removeformat | help',
                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                      }}
                    />
                  </div>
                )}

                {formData.content_type === 'embed' && (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={10}
                      label="Embedded HTML Content"
                      value={formData.content}
                      onChange={(e) => handleEditorChange(e.target.value)}
                      placeholder="Paste your embedded HTML content here (e.g., Loom video embed code)"
                      sx={{ 
                        mb: 2,
                        '& .MuiInputBase-root': {
                          minHeight: '240px', // Approximately 10 lines at default line height
                          fontFamily: 'monospace', // Better for code editing
                          fontSize: '0.9rem'
                        }
                      }}
                    />
                    {formData.content && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview:</Typography>
                        <Box 
                          sx={{ 
                            border: 1,
                            borderColor: 'grey.300',
                            borderRadius: 1,
                            p: 2,
                            bgcolor: 'grey.50',
                            '& iframe': {
                              width: '100%',
                              border: 'none',
                              borderRadius: 1,
                              boxShadow: 1
                            }
                          }}
                        >
                          <Box dangerouslySetInnerHTML={{ __html: formData.content }} />
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {formData.content_type === 'video' && (
                  <TextField
                    fullWidth
                    label="Video URL"
                    value={formData.content_url}
                    onChange={(e) => setFormData({ ...formData, content_url: e.target.value })}
                    placeholder="Enter video URL"
                    sx={{ mb: 2 }}
                  />
                )}

                {formData.content_type === 'pdf' && (
                  <TextField
                    fullWidth
                    label="PDF URL"
                    value={formData.content_url}
                    onChange={(e) => setFormData({ ...formData, content_url: e.target.value })}
                    placeholder="Enter PDF URL"
                    sx={{ mb: 2 }}
                  />
                )}

                <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!hasChanges() || !isFormValid()}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Box>
            ) : (
              null
            )}
          </Paper>
        </>
      )}
    </Container>
  );
};

export default TrainingContentPage; 